import { useState, useEffect, useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import "./UIGenerator.scss";
import Marks from "./components/Marks/Marks";
import UiBlockPanel from "./components/UiBlockPanel/UiBlockPanel";
import BuildArea from "./components/BuildArea/BuildArea";
import { onDragEnd } from "./utils/onDragEnd";
import loadUiBlocks from "./utils/loadUiBlocks";
import Button from "../buttons/Button"
import { buildUrl } from "../utils/helper";
import Detail from "../meta/Detail";
import DialogComponent from "../dialogs/Dialog";
import buildAreaToSchemaMapper from "./utils/buildAreaToSchemaMapper";
import BlockDialog from "./components/Dialogs/BlockDialog";
import SectionDialog from "./components/Dialogs/SectionDialog";
import { getRowId } from "./utils/onDragUtils";
import DeleteDialog from "./components/Dialogs/DeleteDialog";

const UIGenerator = forwardRef((props, ref) => {
  const UIGeneratorRef = useRef();
  useImperativeHandle(ref, () => ({
    element: () => UIGeneratorRef.current,
    validateUI: validateUI,
    // Môžeš pridať aj ďalšie metódy alebo vlastnosti, ktoré chceš, aby rodič mal prístup
  }));

  const { UI, setNewUI, primaryField, componentFactory } = props;

  const detailSchemaWithType = useMemo(() => {
    console.log(props.detailSchema)
    return props.detailSchema.map(x => ({ ...x, ui_type: "db_field" }));
  }, [props.detailSchema]);

  const { schema, remapedUI } = useMemo(() => {
    if (UI) return loadUiBlocks(detailSchemaWithType, UI);
    return { schema: detailSchemaWithType, remapedUI: [[]] };
  }, [UI, detailSchemaWithType]);

  const [marks, setMarks] = useState(remapedUI);
  const [uiBlocks, setUiBlocks] = useState(schema);
  const [dragEnd, setDragEnd] = useState(false);
  const [activeMark, setActiveMark] = useState(0);
  const [isOpenBlockDialog, setIsOpenBlockDialog] = useState(false);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [dragStartRow, setDragStartRow] = useState("");
  const [isClearAreaDialogOpen, setIsClearAreaDialogOpen] = useState(false);

  // buildArea je view do 4D array (marks)
  const buildArea = useMemo(() => marks[activeMark], [marks, activeMark]);

  // takýto prístup JS overloadu kvôli spätnej kompatibilite
  // možnosť volať setBuildArea(func) / setBuildArea(array)
  const setBuildArea = useCallback(
    arg => setMarks(prevMarks => {
      const next = [...prevMarks];

      const newArea =
        typeof arg === "function"
          ? arg(next[activeMark] ?? [])
          : arg;

      next[activeMark] = newArea;
      return next;
    }),
    [activeMark]
  );


  useEffect(() => {
    setNewUI(buildAreaToSchemaMapper(marks));
  }, [marks, setNewUI]);

  useEffect(() => {
    const el = document.querySelector('.AkcUiDesigner');
    if (!el)
      return;
    const z_index = parseInt(window.getComputedStyle(el).zIndex) + 10;
    document.documentElement.style.setProperty('--dnd-clone-z', z_index);
  }, [isClearAreaDialogOpen, isAddSectionOpen, isOpenDetail, isOpenBlockDialog]);

  // Vyfiltrovanie prazdnych riadkov a stlpcov na konci tahania
  useEffect(() => {
    setMarks(prevMarks => {
      const nextMarks = [...prevMarks];
      const original = nextMarks[activeMark] ?? [];
      const cleaned = original.flatMap(row => {
        if (row.type === "line") return [row];
        if (!row.data?.length) return [];
        const filtered = row.data.filter(col => col.data?.length);
        return filtered.length ? [{ ...row, data: filtered }] : [];
      });

      if (cleaned !== original) {
        nextMarks[activeMark] = cleaned;
        return nextMarks;
      }
      return prevMarks;
    });

  }, [dragEnd, activeMark]);


  const validateUI = () => {
    return uiBlocks.length === 0;
  }

  const handleOnDragStart = (result) => {
    setDragStartRow(getRowId(result.source.droppableId));
  }

  const handleOnDragEnd = (result) => {
    setDragEnd((prev) => !prev);
    const { newBuildArea, newUiBlocks } = onDragEnd(result, buildArea, [...uiBlocks]);

    if (newBuildArea !== undefined) setBuildArea(newBuildArea);
    if (newUiBlocks !== undefined) setUiBlocks(newUiBlocks);
  };

  const deleteField = (
    rowIndex,
    columnIndex,
    fieldIndex,
    mark = activeMark
  ) => {
    const deletedField = marks[mark][rowIndex].data[columnIndex].data[fieldIndex];
    if (mark === activeMark) {
      setBuildArea((prev) => {
        prev[rowIndex].data[columnIndex].data = prev[rowIndex].data[columnIndex].data.filter(
          (_, fieldID) => fieldID !== fieldIndex
        );
        return [...prev];
      });
    }
    if (deletedField.ui_type === "db_field")
      setUiBlocks((prev) => [deletedField, ...prev]);
    setDragEnd((prev) => !prev); // Kontrola prazdnych riadkov a stlpcov na vymazanie
  };

  const deleteCol = (rowI, colI) => {
    const col = buildArea[rowI].data[colI].data;
    col.forEach(f => f.ui_type === "db_field" && setUiBlocks(prev => [f, ...prev]))
    setBuildArea(prev => {
      return prev.map((row, rowIndex) => {
        if (rowIndex === rowI) {
          return { type: "row", data: row.data.filter((_, colIndex) => colIndex !== colI) };
        }
        return row;
      });
    });
    setDragEnd(prev => !prev); // Kontrola prazdnych riadkov a stlpcov na vymazanie
  };

  const deleteRow = (rowI) => {
    const row = buildArea[rowI];
    if (row.type === "row") {
      row.data.map(col => col.data.forEach(f => f.ui_type === "db_field" && setUiBlocks(prev => [f, ...prev])))
    }
    setBuildArea(prev => prev.filter((_, index) => index !== rowI));
    setDragEnd((prev) => !prev); // Kontrola prazdnych riadkov a stlpcov na vymazanie
  }

  const addMark = () => {
    if (marks.length > 9) return;

    setMarks((prev) => [...prev, []]);
    setActiveMark(marks.length); // Nastavenie novej zalozky ako aktivnej
  };

  const clearBuildArea = () => {
    buildArea.forEach((row, _) => {
      if (row.type === "row")
        row.data.forEach((col, _) => {
          if (col.type === "column")
            col.data.forEach((field, _) => {
              if (field.ui_type === "db_field")
                setUiBlocks((prev) => [field, ...prev])
            })
        })
    })
    setBuildArea([]);
  }

  const deleteMark = (index) => {
    const markToBeDeleted = marks[index];
    markToBeDeleted.forEach((row) => {
      if (row.type === "row")
        row.data.forEach((col) => {
          if (col.type === "column")
            col.data.forEach((field) => {
              if (field.ui_type === "db_field")
                setUiBlocks((prev) => [field, ...prev])
            });
        });
    });
    setMarks((prevMarks) => prevMarks.filter((_, i) => i !== index));
    if (index === activeMark) {
      setActiveMark(Math.max(0, index - 1));
    } else if (index < activeMark) {
      setActiveMark((prev) => prev - 1);
    }
  };

  return (
    <div ref={UIGeneratorRef} style={{ minHeight: "65vh", maxHeight: "65vh" }}>
      <DragDropContext onDragStart={handleOnDragStart} onDragEnd={handleOnDragEnd}>
        <div className="UIGenerator">
          {isOpenBlockDialog &&
            <BlockDialog
              setOpen={setIsOpenBlockDialog}
              isOpen={isOpenBlockDialog}
              uiBlocks={uiBlocks}
              setBuildArea={setBuildArea}
              buildArea={buildArea}
              setUiBlocks={setUiBlocks}
              count={0}
              fieldToMake={2} // 2 - ROW
            />}
          {
            isAddSectionOpen && <SectionDialog setOpen={setIsAddSectionOpen} isOpen={isAddSectionOpen} setBuildArea={setBuildArea} count={"0"} />
          }
          {
            isClearAreaDialogOpen && <DeleteDialog setOpen={setIsClearAreaDialogOpen} isOpen={isClearAreaDialogOpen} onConfirm={() => clearBuildArea()} />
          }
          <>
            <UiBlockPanel uiBlocks={uiBlocks} />
            <div className="right-panel-wrapper">
              <div>
                <Marks
                  marks={marks}
                  activeMark={activeMark}
                  addMark={addMark}
                  switchMark={(index) => setActiveMark(index)}
                  deleteMark={deleteMark}
                  buildArea={buildArea}
                />
                <div className="utils-container">
                  <div className="utils-wrapper">
                    <button className="add-component" onClick={() => { setIsOpenBlockDialog(true) }}>
                      <span id="button-plus">+</span><span id="button-title">Komponent</span>
                    </button>
                    <button className="add-section" onClick={() => setIsAddSectionOpen(true)}>
                      <span id="button-plus">+</span><span id="button-title">Sekcia</span>
                    </button>
                    <Button
                      onClick={() => {
                        if (buildArea.length !== 0)
                          setIsClearAreaDialogOpen(true)
                      }}
                      primary={true}
                      imageUrl={buildUrl("assets/icons/toolbar/delete.svg")}
                      title="Vyčistiť plochu"
                      className="button-remove"
                    />
                    <span id="delimiter"></span>
                  </div>
                  <div className="preview-wrapper">
                    <Button
                      onClick={() => {
                        setNewUI(() => buildAreaToSchemaMapper(marks));
                        setIsOpenDetail(true)
                      }}
                      primary={true}
                      imageUrl={buildUrl("assets/icons/toolbar/image.svg")}
                      title="Náhľad formulára"
                      className="preview-button"
                    />
                  </div>
                </div>
              </div>
              <BuildArea
                buildArea={buildArea}
                deleteField={deleteField}
                deleteCol={deleteCol}
                deleteRow={deleteRow}
                setBuildArea={setBuildArea}
                setOpen={setIsOpenBlockDialog}
                uiBlocks={uiBlocks}
                setUiBlocks={setUiBlocks}
                activeMark={activeMark}
                dragStartRow={dragStartRow}
              />

            </div></>
          {isOpenDetail &&
            <DialogComponent
              isOpen={isOpenDetail}
              onClose={() => { setIsOpenDetail(false) }}
              title={"Náhľad formulára"}
              hideButtons
              fullscreen
            >
              <Detail
                onSubmit={() => { }}
                detailData={{}}
                onChangeField={() => { }}
                allData={false}
                isDetailLoading={false}
                onGet={() => { }}
                onCreate={() => { }}
                onUpdate={() => { }}
                onDiscard={() => { }}
                onBlur={() => { }}
                getList={() => { }}
                primaryField={primaryField}
                schema={props.detailSchema}
                disabled={true}
                ui={UI}
                toolbarHidden={true}
                hiddenBack={true}
                componentFactory={componentFactory}
              />
            </DialogComponent>
          }

        </div>
      </DragDropContext>
    </div>
  );
});

export default UIGenerator;
